import { useAtom } from 'jotai';
import { userAtom, accessTokenAtom } from '../../../../atoms';
import { Navigate } from 'react-router-dom';
import { paths } from '../../../../../config/paths';
import { useCallback, useEffect, useState, FormEvent } from 'react';

interface UserDetail {
    id: string;
    username: string;
    email: string;
    parent_user_id: string | null;
    user_fullpath: string;
    depth: number;
    fujiyama_telegram_id: string | null;
    fujiyama_referral_code: string | null;
    link_type: string;
    is_debit_card_purchased: boolean;
    debit_referral_code: string | null;
    debit_distributor_code: string | null;
    country: string | null;
    is_locked: boolean;
    createdAt: string;
}

interface SearchUsersResponse {
    users: UserDetail[];
    total: number;
    limit: number;
    offset: number;
}

interface SearchParams {
    id?: string;
    username?: string;
    email?: string;
    depth?: string;
    parent_user_id?: string;
    telegram_id?: string;
    fujiyama_referral_code?: string;
    link_type?: string;
    is_debit_card_purchased?: string;
    debit_referral_code?: string;
    country?: string;
    is_locked?: string;
    created_at_from?: string;
    created_at_to?: string;
    limit?: string;
    offset?: string;
}

const useSearchUsers = (searchParams: SearchParams, page: number) => {
    const [user] = useAtom(userAtom);
    const [accessToken] = useAtom(accessTokenAtom);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [data, setData] = useState<SearchUsersResponse | null>(null);

    const fetchUsers = useCallback(async () => {
        if (!user || !accessToken) {
            setError('認証情報が見つかりません');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            // クエリパラメータの構築
            const queryParams = new URLSearchParams();

            // 空でない値のみクエリパラメータに追加
            Object.entries(searchParams).forEach(([key, value]) => {
                if (value && value.trim() !== '') {
                    queryParams.append(key, value);
                }
            });

            // ページネーション用のパラメータ
            const offset = (page - 1) * (parseInt(searchParams.limit || '50'));
            queryParams.set('offset', offset.toString());

            const response = await fetch(`/api/v1/admin/users?${queryParams.toString()}`, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                throw new Error('ユーザー情報の取得に失敗しました');
            }

            const responseData: SearchUsersResponse = await response.json();
            setData(responseData);
        } catch (err) {
            setError(err instanceof Error ? err.message : '予期せぬエラーが発生しました');
        } finally {
            setIsLoading(false);
        }
    }, [user, accessToken, searchParams, page]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    return {
        data,
        isLoading,
        error,
        refetch: fetchUsers
    };
};

export const SearchUsersPage = () => {
    const [user] = useAtom(userAtom);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchParams, setSearchParams] = useState<SearchParams>({
        limit: '50'
    });

    const {
        data,
        isLoading,
        error,
        refetch
    } = useSearchUsers(searchParams, currentPage);

    if (!user?.has_admin_role) {
        return <Navigate to={paths.dashboard.path} replace />;
    }

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(prev => prev - 1);
        }
    };

    const handleNextPage = () => {
        if (data && data.offset + data.users.length < data.total) {
            setCurrentPage(prev => prev + 1);
        }
    };

    const handleSearch = (e: FormEvent) => {
        e.preventDefault();
        setCurrentPage(1); // 検索時はページを1に戻す
        refetch();
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setSearchParams(prev => ({
            ...prev,
            [name]: value
        }));
    };

    return (
        <div className="max-w-6xl mx-auto">
            <h1 className="text-2xl font-bold mb-6">ユーザー検索</h1>

            {/* 検索フォーム */}
            <div className="bg-white shadow rounded-lg p-6 mb-6">
                <form onSubmit={handleSearch} className="space-y-6">
                    {/* Debit Cardグループ */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                        <h2 className="text-lg font-medium text-gray-800 mb-3">Debit Card系</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-gray-700">メールアドレス</label>
                                <input
                                    type="text"
                                    id="email"
                                    name="email"
                                    value={searchParams.email || ''}
                                    onChange={handleInputChange}
                                    className="w-full p-2 border rounded-md"
                                />
                            </div>

                            <div>
                                <label htmlFor="username" className="block text-sm font-medium text-gray-700">ユーザー名</label>
                                <input
                                    type="text"
                                    id="username"
                                    name="username"
                                    value={searchParams.username || ''}
                                    onChange={handleInputChange}
                                    className="w-full p-2 border rounded-md"
                                />
                            </div>

                            <div>
                                <label htmlFor="country" className="block text-sm font-medium text-gray-700">国</label>
                                <select
                                    id="country"
                                    name="country"
                                    value={searchParams.country || ''}
                                    onChange={handleInputChange}
                                    className="w-full p-2 border rounded-md"
                                >
                                    <option value="">選択してください</option>
                                    <option value="Japan">Japan</option>
                                    <option value="United States of America">United States of America</option>
                                    <option value="China">China</option>
                                    <option value="Korea South">Korea South</option>
                                    <option value="Taiwan">Taiwan</option>
                                    <option value="Singapore">Singapore</option>
                                    <option value="Hong Kong">Hong Kong</option>
                                    <option value="Thailand">Thailand</option>
                                    <option value="Vietnam">Vietnam</option>
                                    <option value="Malaysia">Malaysia</option>
                                    <option value="Indonesia">Indonesia</option>
                                    <option value="Philippines">Philippines</option>
                                    <option value="India">India</option>
                                    <option value="Australia">Australia</option>
                                    <option value="United Kingdom">United Kingdom</option>
                                    <option value="Germany">Germany</option>
                                    <option value="France">France</option>
                                    <option value="Italy">Italy</option>
                                    <option value="Spain">Spain</option>
                                    <option value="Canada">Canada</option>
                                </select>
                            </div>

                            <div>
                                <label htmlFor="depth" className="block text-sm font-medium text-gray-700">深さ</label>
                                <input
                                    type="number"
                                    id="depth"
                                    name="depth"
                                    value={searchParams.depth || ''}
                                    onChange={handleInputChange}
                                    className="w-full p-2 border rounded-md"
                                />
                            </div>

                            <div>
                                <label htmlFor="parent_user_id" className="block text-sm font-medium text-gray-700">親ユーザーID</label>
                                <input
                                    type="text"
                                    id="parent_user_id"
                                    name="parent_user_id"
                                    value={searchParams.parent_user_id || ''}
                                    onChange={handleInputChange}
                                    className="w-full p-2 border rounded-md"
                                />
                            </div>

                            <div>
                                <label htmlFor="debit_referral_code" className="block text-sm font-medium text-gray-700">Fujiyama Debit Ref Code</label>
                                <input
                                    type="text"
                                    id="debit_referral_code"
                                    name="debit_referral_code"
                                    value={searchParams.debit_referral_code || ''}
                                    onChange={handleInputChange}
                                    className="w-full p-2 border rounded-md"
                                />
                            </div>

                            <div>
                                <label htmlFor="link_type" className="block text-sm font-medium text-gray-700">紹介タイプ</label>
                                <select
                                    id="link_type"
                                    name="link_type"
                                    value={searchParams.link_type || ''}
                                    onChange={handleInputChange}
                                    className="w-full p-2 border rounded-md"
                                >
                                    <option value="">選択してください</option>
                                    <option value="referral_code">紹介なし</option>
                                    <option value="direct">直紹介</option>
                                    <option value="distributor_code">代理店リンク</option>
                                </select>
                            </div>

                            <div>
                                <label htmlFor="is_debit_card_purchased" className="block text-sm font-medium text-gray-700">デビットカード支払確認済</label>
                                <select
                                    id="is_debit_card_purchased"
                                    name="is_debit_card_purchased"
                                    value={searchParams.is_debit_card_purchased || ''}
                                    onChange={handleInputChange}
                                    className="w-full p-2 border rounded-md"
                                >
                                    <option value="">選択してください</option>
                                    <option value="true">はい</option>
                                    <option value="false">いいえ</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Fujiyama Tapグループ */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                        <h2 className="text-lg font-medium text-gray-800 mb-3">Fujiyama Tap系</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="telegram_id" className="block text-sm font-medium text-gray-700">Telegram ID</label>
                                <input
                                    type="text"
                                    id="telegram_id"
                                    name="telegram_id"
                                    value={searchParams.telegram_id || ''}
                                    onChange={handleInputChange}
                                    className="w-full p-2 border rounded-md"
                                />
                            </div>

                            <div>
                                <label htmlFor="fujiyama_referral_code" className="block text-sm font-medium text-gray-700">Fujiyama Tap Ref Code</label>
                                <input
                                    type="text"
                                    id="fujiyama_referral_code"
                                    name="fujiyama_referral_code"
                                    value={searchParams.fujiyama_referral_code || ''}
                                    onChange={handleInputChange}
                                    className="w-full p-2 border rounded-md"
                                />
                            </div>
                        </div>
                    </div>

                    {/* 絞り込み条件 */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                        <h2 className="text-lg font-medium text-gray-800 mb-3">絞り込み条件</h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label htmlFor="created_at_from" className="block text-sm font-medium text-gray-700">作成日（開始）</label>
                                <input
                                    type="datetime-local"
                                    id="created_at_from"
                                    name="created_at_from"
                                    value={searchParams.created_at_from || ''}
                                    onChange={handleInputChange}
                                    className="w-full p-2 border rounded-md"
                                />
                            </div>

                            <div>
                                <label htmlFor="created_at_to" className="block text-sm font-medium text-gray-700">作成日（終了）</label>
                                <input
                                    type="datetime-local"
                                    id="created_at_to"
                                    name="created_at_to"
                                    value={searchParams.created_at_to || ''}
                                    onChange={handleInputChange}
                                    className="w-full p-2 border rounded-md"
                                />
                            </div>

                            <div>
                                <label htmlFor="limit" className="block text-sm font-medium text-gray-700">表示件数</label>
                                <select
                                    id="limit"
                                    name="limit"
                                    value={searchParams.limit || '50'}
                                    onChange={handleInputChange}
                                    className="w-full p-2 border rounded-md"
                                >
                                    <option value="10">10件</option>
                                    <option value="20">20件</option>
                                    <option value="50">50件</option>
                                    <option value="100">100件</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end">
                        <button
                            type="submit"
                            className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            検索
                        </button>
                    </div>
                </form>
            </div>

            {/* 検索件数とページネーション */}
            {data && (
                <div className="bg-blue-50 shadow rounded-lg p-4 mb-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div>
                            <p className="text-blue-700 font-medium">
                                検索結果: {data.total}件（{data.offset + 1}-{Math.min(data.offset + data.users.length, data.total)}件を表示）
                            </p>
                        </div>
                        <div className="flex items-center space-x-2 flex-wrap">
                            {/* 最初のページへ */}
                            <button
                                onClick={() => setCurrentPage(1)}
                                disabled={currentPage === 1}
                                className={`px-3 py-1 border rounded-md ${currentPage === 1
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'bg-white text-gray-700 hover:bg-gray-50'
                                    }`}
                            >
                                ≪
                            </button>

                            {/* 前へ */}
                            <button
                                onClick={handlePreviousPage}
                                disabled={currentPage === 1}
                                className={`px-3 py-1 border rounded-md ${currentPage === 1
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'bg-white text-gray-700 hover:bg-gray-50'
                                    }`}
                            >
                                前へ
                            </button>

                            {/* ページ番号ボタン */}
                            {(() => {
                                const totalPages = Math.ceil(data.total / parseInt(searchParams.limit || '50'));
                                const pageButtons = [];

                                // 表示するページボタンの範囲を決定
                                let startPage = Math.max(1, currentPage - 4);
                                const endPage = Math.min(totalPages, startPage + 9);

                                // 最大10個のボタンを表示するように調整
                                if (endPage - startPage < 9) {
                                    startPage = Math.max(1, endPage - 9);
                                }

                                for (let i = startPage; i <= endPage; i++) {
                                    pageButtons.push(
                                        <button
                                            key={i}
                                            onClick={() => setCurrentPage(i)}
                                            className={`px-3 py-1 border rounded-md ${currentPage === i
                                                ? 'bg-blue-600 text-white'
                                                : 'bg-white text-gray-700 hover:bg-gray-50'
                                                }`}
                                        >
                                            {i}
                                        </button>
                                    );
                                }

                                return pageButtons;
                            })()}

                            {/* 次へ */}
                            <button
                                onClick={handleNextPage}
                                disabled={data.offset + data.users.length >= data.total}
                                className={`px-3 py-1 border rounded-md ${data.offset + data.users.length >= data.total
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'bg-white text-gray-700 hover:bg-gray-50'
                                    }`}
                            >
                                次へ
                            </button>

                            {/* 最後のページへ */}
                            <button
                                onClick={() => {
                                    const totalPages = Math.ceil(data.total / parseInt(searchParams.limit || '50'));
                                    setCurrentPage(totalPages);
                                }}
                                disabled={data.offset + data.users.length >= data.total}
                                className={`px-3 py-1 border rounded-md ${data.offset + data.users.length >= data.total
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'bg-white text-gray-700 hover:bg-gray-50'
                                    }`}
                            >
                                ≫
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 検索結果 */}
            <div className="bg-white shadow rounded-lg p-6">
                <div className="space-y-4">
                    {error && (
                        <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-4">
                            {error}
                        </div>
                    )}

                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        基本情報
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Debit Card系
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Fujiyama Tap系
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {isLoading ? (
                                    <tr>
                                        <td colSpan={3} className="px-6 py-4 text-center">
                                            <div className="flex justify-center items-center">
                                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                                            </div>
                                        </td>
                                    </tr>
                                ) : data?.users.length === 0 ? (
                                    <tr>
                                        <td colSpan={3} className="px-6 py-4 text-center text-gray-500">
                                            ユーザーが見つかりませんでした
                                        </td>
                                    </tr>
                                ) : (
                                    data?.users.map((user) => (
                                        <tr key={user.id}>
                                            <td className="px-6 py-4">
                                                <div className="text-sm">
                                                    <div className="font-medium text-gray-900">{user.username}</div>
                                                    <div className="text-gray-500">{user.email}</div>
                                                    <div className="text-gray-500 font-medium">ID: <span className="font-normal">{user.id}</span></div>
                                                    <div className="text-gray-500 font-medium">作成日: <span className="font-normal">{new Date(user.createdAt).toLocaleString('ja-JP')}</span></div>
                                                    {user.is_locked && (
                                                        <div className="text-red-500 font-medium">ロック中</div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="text-sm">
                                                    <div className="text-gray-500 font-medium">
                                                        購入済み: <span className="font-normal">{user.is_debit_card_purchased ? 'はい' : 'いいえ'}</span>
                                                    </div>
                                                    {user.debit_referral_code && (
                                                        <div className="text-gray-500 font-medium">Referral Code: <span className="font-normal">{user.debit_referral_code}</span></div>
                                                    )}
                                                    {user.debit_distributor_code && (
                                                        <div className="text-gray-500 font-medium">Distributor Code: <span className="font-normal">{user.debit_distributor_code}</span></div>
                                                    )}
                                                    {user.country && (
                                                        <div className="text-gray-500 font-medium">国: <span className="font-normal">{user.country}</span></div>
                                                    )}
                                                    <div className="text-gray-500 font-medium">深さ: <span className="font-normal">{user.depth}</span></div>
                                                    <div className="text-gray-500 font-medium">リンクタイプ: <span className="font-normal">{user.link_type}</span></div>
                                                    <div className="text-gray-500 font-medium">親ID: <span className="font-normal">{user.parent_user_id || 'なし'}</span></div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="text-sm">
                                                    {user.fujiyama_telegram_id && (
                                                        <div className="text-gray-500 font-medium">Telegram ID: <span className="font-normal">{user.fujiyama_telegram_id}</span></div>
                                                    )}
                                                    {user.fujiyama_referral_code && (
                                                        <div className="text-gray-500 font-medium">Fujiyama紹介コード: <span className="font-normal">{user.fujiyama_referral_code}</span></div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* ページネーション */}
                    {data && (
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between mt-4 gap-4">
                            <div className="flex items-center">
                                <span className="text-sm text-gray-700">
                                    全{data.total}件中 {data.offset + 1}-
                                    {Math.min(data.offset + data.users.length, data.total)}件を表示
                                </span>
                            </div>
                            <div className="flex items-center space-x-2 flex-wrap">
                                {/* 最初のページへ */}
                                <button
                                    onClick={() => setCurrentPage(1)}
                                    disabled={currentPage === 1}
                                    className={`px-3 py-1 border rounded-md ${currentPage === 1
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : 'bg-white text-gray-700 hover:bg-gray-50'
                                        }`}
                                >
                                    ≪
                                </button>

                                {/* 前へ */}
                                <button
                                    onClick={handlePreviousPage}
                                    disabled={currentPage === 1}
                                    className={`px-3 py-1 border rounded-md ${currentPage === 1
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : 'bg-white text-gray-700 hover:bg-gray-50'
                                        }`}
                                >
                                    前へ
                                </button>

                                {/* ページ番号ボタン */}
                                {(() => {
                                    const totalPages = Math.ceil(data.total / parseInt(searchParams.limit || '50'));
                                    const pageButtons = [];

                                    // 表示するページボタンの範囲を決定
                                    let startPage = Math.max(1, currentPage - 4);
                                    const endPage = Math.min(totalPages, startPage + 9);

                                    // 最大10個のボタンを表示するように調整
                                    if (endPage - startPage < 9) {
                                        startPage = Math.max(1, endPage - 9);
                                    }

                                    for (let i = startPage; i <= endPage; i++) {
                                        pageButtons.push(
                                            <button
                                                key={i}
                                                onClick={() => setCurrentPage(i)}
                                                className={`px-3 py-1 border rounded-md ${currentPage === i
                                                    ? 'bg-blue-600 text-white'
                                                    : 'bg-white text-gray-700 hover:bg-gray-50'
                                                    }`}
                                            >
                                                {i}
                                            </button>
                                        );
                                    }

                                    return pageButtons;
                                })()}

                                {/* 次へ */}
                                <button
                                    onClick={handleNextPage}
                                    disabled={data.offset + data.users.length >= data.total}
                                    className={`px-3 py-1 border rounded-md ${data.offset + data.users.length >= data.total
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : 'bg-white text-gray-700 hover:bg-gray-50'
                                        }`}
                                >
                                    次へ
                                </button>

                                {/* 最後のページへ */}
                                <button
                                    onClick={() => {
                                        const totalPages = Math.ceil(data.total / parseInt(searchParams.limit || '50'));
                                        setCurrentPage(totalPages);
                                    }}
                                    disabled={data.offset + data.users.length >= data.total}
                                    className={`px-3 py-1 border rounded-md ${data.offset + data.users.length >= data.total
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : 'bg-white text-gray-700 hover:bg-gray-50'
                                        }`}
                                >
                                    ≫
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SearchUsersPage;